import AdapterError from '@ember-data/adapter/error';
import JSONAPIAdapter from '@ember-data/adapter/json-api';
import { camelize, pluralize } from '@ember-data/request-utils/string';
import { service } from '@ember/service';
import param from 'jquery-param';
import Config, { type IEmberSimpleAuthTokenConfig } from 'volta/config/environment';

import type { RequestData } from '@ember-data/adapter/rest';
import type { AdapterPayload } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/legacy-network-handler/snapshot';
import type { ModelSchema } from '@ember-data/store/types';
import type { HTTPMethod } from '@warp-drive/core-types/request';
import type ModelRegistry from 'ember-data/types/registries/model';
import type SessionService from 'ember-simple-auth/services/session';
import type StoreService from 'volta/services/store';
import type { IHeaders } from 'volta/utils/api/types';
import type { IntlService } from 'ember-intl';

const DEFAULT_HEADERS: IHeaders = {
  'X-Requested-With': 'XMLHttpRequest'
};

type Payload = Error | Record<string, unknown> | unknown[] | string | undefined;

export default class ApplicationAdapter extends JSONAPIAdapter {
  // Services
  // ~~~~~

  @service declare session: SessionService;
  @service declare intl: IntlService;

  // Properties
  // ~~~~~

  // API config
  host = Config.api.host;
  namespace = Config.api.namespace;
  authConfig: IEmberSimpleAuthTokenConfig = Config['ember-simple-auth-token'];

  useFetch = true;

  // Computed Properties
  // ~~~~~

  // @ts-ignore
  get headers() {
    const defaultHeaders = DEFAULT_HEADERS;
    if (this.session.isAuthenticated) {
      defaultHeaders[this.authConfig.authorizationHeaderName] = this.getSessionToken();
      defaultHeaders['Accept-Language'] = this.intl.primaryLocale || 'en-GB';
    }
    return defaultHeaders;
  }

  // Helper Functions
  // ~~~~~

  getSessionToken() {
    return this.session.data?.authenticated?.[this.authConfig.tokenPropertyName];
  }

  getAuthenticatedHeaders() {
    const authHeaders: Record<string, string> = {};
    authHeaders[this.authConfig.authorizationHeaderName] = this.getSessionToken();
    return { ...this.headers, ...authHeaders };
  }

  buildRawURL(url: string) {
    return `${this.host}/${this.namespace}/${url}`;
  }

  buildNestedUrl(parentModelName: string, parentEntityId: string, childModelName: string) {
    return this.buildRawURL(`${parentModelName}/${parentEntityId}/${childModelName}`);
  }

  /**
   * Because dataType:json is hardcoded we are looking
   * for either a blob:true or arraybuffer:true ajax option
   * in order to set the proper data type to jQuery.
   *
   */
  ajaxOptions(
    url: string,
    type: HTTPMethod,
    options: { arraybuffer?: boolean; blob?: boolean; binary?: boolean }
  ) {
    const opts: any = super.ajaxOptions(url, type, options);
    if (options) {
      if (options.arraybuffer) {
        opts.dataType = 'arraybuffer';
        opts.processData = false;
      } else if (options.blob) {
        opts.dataType = 'blob';
        opts.processData = false;
      } else if (options.binary) {
        opts.dataType = 'binary';
        opts.processData = false;
      }
    }
    return opts;
  }

  // Ember Data Adapter Hooks
  // ~~~~~

  pathForType(modelName: string): string {
    return pluralize(camelize(modelName));
  }

  urlForFindRecord<K extends keyof ModelRegistry>(
    id: string,
    modelName: string,
    snapshot: Snapshot<K>
  ): string {
    let url = super.urlForFindRecord(id, modelName, snapshot);
    if (snapshot) {
      const query = (snapshot.adapterOptions as Record<string, any> | undefined)?.['query'];
      if (query) {
        url += '?' + param(query); // assumes no query params are present already
      }
    }
    return url;
  }

  updateRecord(
    store: StoreService,
    type: ModelSchema,
    snapshot: Snapshot
  ): Promise<AdapterPayload> {
    if (snapshot.adapterOptions?.['rehydrate']) {
      const serializer = store.serializerFor(snapshot.modelName);
      let data: object;
      if (serializer) {
        data = {};

        if (typeof serializer.serializeIntoHash === 'function') {
          serializer.serializeIntoHash(data, type, snapshot);
        }
        data = serializer.serialize(snapshot, {});
      }

      return new Promise((resolve) => resolve({ data }));
    } else {
      return super.updateRecord(store, type, snapshot);
    }
  }

  /**
   * Takes an ajax response, and returns the json payload or an error.
   */
  handleResponse(
    status: number,
    headers: Record<string, string>,
    payload: Payload,
    requestData: RequestData
  ): Payload | typeof AdapterError {
    if (status === 401 && this.session.isAuthenticated) {
      this.session.invalidate();
    }
    return super.handleResponse(status, headers, payload, requestData);
  }
}
